import { Tooltip } from '@forethought-technologies/forethought-elements';
import { useAppDispatch, useAppSelector } from 'hooks';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import {
  selectIsFeedbackTooltipPromptable,
  setCsatStatesByKey,
} from 'reducers/workflows';

/**
 * this tooltip would automatically show its content for 3 seconds when the
 * component is first rendered and satisfies some conditions
 */
export const PromptFeedbackTooltip = ({
  children,
  tooltipContent,
}: {
  children: React.ReactElement;
  tooltipContent: string;
}) => {
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);
  const isFeedbackTooltipPromptable = useAppSelector(
    selectIsFeedbackTooltipPromptable,
  );
  const closeTimeoutId = useRef<NodeJS.Timeout>();

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);

    if (closeTimeoutId.current) {
      clearTimeout(closeTimeoutId.current);
    }
  };

  useEffect(() => {
    if (isFeedbackTooltipPromptable) {
      dispatch(setCsatStatesByKey({ hasFeedbackTooltipPrompted: true }));
      // prompt the tooltip after 0.5 seconds
      const openTimeoutId = setTimeout(() => {
        setOpen(true);
      }, 500);

      // hide the tooltip after 3.5 seconds, i.e. the tooltip stays visible for 3 seconds
      closeTimeoutId.current = setTimeout(() => {
        setOpen(false);
      }, 3500);

      return () => {
        /**
         * need isFeedbackTooltipPromptable here too, otherwise the component would re-render
         * after the timer is created and clear it right away
         * */
        if (closeTimeoutId.current && !isFeedbackTooltipPromptable) {
          clearTimeout(openTimeoutId);
          clearTimeout(closeTimeoutId.current);
        }
      };
    }
  }, [dispatch, isFeedbackTooltipPromptable]);

  return (
    <Tooltip
      onClose={handleClose}
      onOpen={handleOpen}
      open={open}
      tooltipContent={tooltipContent}
    >
      {children}
    </Tooltip>
  );
};
